import api from './api';

export interface Bank {
  // Note: `/cards/banks/?country=TT` may return "virtual" banks (not in DB yet) with `id: null`.
  id: number | null;
  name: string;
  code: string;
  country: string;
  website: string;

  // Backend-computed classification (separate from `bank_type`)
  operating_category?: 'TRADITIONAL' | 'NON_TRADITIONAL' | 'UNKNOWN';
  is_traditional_bank?: boolean;

  // Optional fields that can be present from BankSerializer / virtual banks
  bank_type?: string;
  is_active?: boolean;
  is_available?: boolean; // virtual banks from scraper may include this flag
}

export interface CreditCard {
  id: number;
  name: string;
  bank: number | Bank;
  card_type: string;
  annual_fee: number;
  interest_rate?: number | null;
  credit_limit_min?: number | null;
  credit_limit_max?: number | null;
  reward_points_rate?: number;
  cashback_rate?: number;
  reward_currency_basis?: string;
  reward_type?: 'cashback' | 'points' | 'miles';
  welcome_bonus?: string | null;
  requirements?: string | null;
  features?: string | null;
  image_url?: string | null;
  is_active: boolean;
  scraping_url?: string | null;
  last_scraped?: string | null;
}

export interface UserCard {
  id: number;
  card: CreditCard;
  card_number_last4: string;
  expiry_date: string;
  is_primary: boolean;
  is_active: boolean;
  linked_at: string;
}

export interface CardFilters {
  bank?: number | null;
  card_type?: string;
  search?: string;
  show_all?: boolean;
}

export const cardService = {
  getBanks: async (): Promise<Bank[]> => {
    const response = await api.get('/cards/banks/?country=TT');
    return response.data;
  },

  getCards: async (filters?: CardFilters): Promise<CreditCard[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.bank) params.append('bank', filters.bank.toString());
      if (filters?.card_type) params.append('card_type', filters.card_type);
      if (filters?.search) params.append('search', filters.search);
      if (filters?.show_all) params.append('show_all', 'true');
      
      // Backend mounts cards app at /api/cards/, and CreditCardListView at path 'cards/'
      // So the full endpoint is /api/cards/cards/
      const response = await api.get(`/cards/cards/?${params.toString()}`);
      
      // Handle both array and object responses
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        return response.data.results;
      } else if (response.data?.cards && Array.isArray(response.data.cards)) {
        return response.data.cards;
      }
      
      console.warn('Unexpected response format from getCards:', response.data);
      return [];
    } catch (error: any) {
      console.error('Error fetching cards:', error);
      throw error;
    }
  },

  getUserCards: async (): Promise<UserCard[]> => {
    const response = await api.get('/cards/user-cards/');
    return response.data;
  },

  addUserCard: async (cardId: number, cardNumberLastFour: string, expiryDate: string): Promise<UserCard> => {
    const response = await api.post('/cards/user-cards/', {
      card: cardId,
      card_number_last4: cardNumberLastFour,
      expiry_date: expiryDate,
    });
    return response.data;
  },

  updateUserCard: async (userCardId: number, data: Partial<UserCard>): Promise<UserCard> => {
    const response = await api.patch(`/cards/user-cards/${userCardId}/`, data);
    return response.data;
  },

  deleteUserCard: async (userCardId: number): Promise<void> => {
    await api.delete(`/cards/user-cards/${userCardId}/`);
  },
};
